import React, { useEffect, useState, useRef } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { toast } from 'react-toastify';
import {
    getRewards,
    createReward,
    updateReward,
    IRewardItem,
} from 'services/reward.service';
import { handleError, createStyleSheet } from 'utils/helpers';
import { useForm, Controller, useFormContext } from 'react-hook-form';
import FormProvider from 'common/components/form-provider';
import BaseTextField from 'common/components/base-text-field';
import RequiredLabel from 'common/components/required-label';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { colors, styleInput } from 'common/constants';

// ==================== ImageUpload Component ====================
interface ImageUploadProps {
    name: string;
    sx?: object;
}

const ImageUpload: React.FC<ImageUploadProps> = ({ name, sx }) => {
    const { control, setValue, watch } = useFormContext();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const imageValue = watch(name);

    // Lấy URL để preview
    const getPreviewUrl = (): string | null => {
        if (!imageValue) return null;
        if (typeof imageValue === 'string' && imageValue.length > 0) {
            return imageValue;
        }
        if (imageValue instanceof File) {
            return URL.createObjectURL(imageValue);
        }
        return null;
    };

    const previewUrl = getPreviewUrl();

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                toast.error('Vui lòng chọn file ảnh');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Kích thước ảnh tối đa 5MB');
                return;
            }
            setValue(name, file);
        }
        event.target.value = '';
    };

    const handleRemove = (e: React.MouseEvent) => {
        e.stopPropagation();
        setValue(name, '');
    };

    return (
        <Controller
            name={name}
            control={control}
            render={() => (
                <Box sx={{ position: 'relative', ...sx }}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />

                    {previewUrl ? (
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                borderRadius: 2,
                                overflow: 'hidden',
                                position: 'relative',
                                '&:hover .overlay': {
                                    opacity: 1,
                                },
                            }}
                        >
                            <Box
                                component="img"
                                src={previewUrl}
                                alt="Preview"
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                }}
                            />
                            <Box
                                className="overlay"
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                }}
                            >
                                <IconButton
                                    size="small"
                                    onClick={handleClick}
                                    sx={{
                                        backgroundColor: 'white',
                                        '&:hover': {
                                            backgroundColor: '#f5f5f5',
                                        },
                                    }}
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={handleRemove}
                                    sx={{
                                        backgroundColor: 'white',
                                        color: 'error.main',
                                        '&:hover': {
                                            backgroundColor: '#ffebee',
                                        },
                                    }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    ) : (
                        <Box
                            onClick={handleClick}
                            sx={{
                                width: '100%',
                                height: '100%',
                                borderRadius: 2,
                                border: `2px dashed ${colors.main}`,
                                backgroundColor: '#f5f5f5',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    borderColor: 'primary.main',
                                    backgroundColor: '#e3f2fd',
                                },
                            }}
                        >
                            <CloudUploadIcon
                                sx={{ fontSize: 40, color: colors.main, mb: 1 }}
                            />
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Tải ảnh lên
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        />
    );
};

// ==================== Main Component ====================
interface IRewardForm {
    name: string;
    image: string | File;
    detail: string;
}

const rewardSchema = yup.object().shape({
    name: yup.string().required('Vui lòng nhập tên phần thưởng'),
    image: yup.mixed().nullable(),
    detail: yup.string().default(''),
});

const Reward: React.FC = () => {
    const [rewards, setRewards] = useState<IRewardItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingReward, setEditingReward] = useState<IRewardItem | null>(
        null
    );

    const methods = useForm<IRewardForm>({
        defaultValues: {
            name: '',
            image: '',
            detail: '',
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        resolver: yupResolver(rewardSchema) as any,
    });

    const { handleSubmit, reset } = methods;

    const fetchRewards = async () => {
        setLoading(true);
        try {
            const response = await getRewards();
            if (response.success && response.data?.rewards) {
                setRewards(response.data.rewards);
            }
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };
    console.log(methods.formState.errors);
    useEffect(() => {
        fetchRewards();
    }, []);

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenCreate = () => {
        setEditingReward(null);
        reset({ name: '', image: '', detail: '' });
        setOpenDialog(true);
    };

    const handleOpenEdit = (reward: IRewardItem) => {
        setEditingReward(reward);
        reset({
            name: reward.attributes.name,
            image: reward.attributes.image.url,
            detail: reward.attributes.detail,
        });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingReward(null);
        reset({ name: '', image: '', detail: '' });
    };

    const onSubmit = handleSubmit(async (data: IRewardForm) => {
        try {
            const formData = new FormData();
            formData.append('name', data.name);
            formData.append('detail', data.detail || '');

            if (data.image instanceof File) {
                formData.append('image', data.image);
            }

            if (editingReward) {
                const response = await updateReward(
                    editingReward.attributes.id,
                    formData
                );
                if (response.success) {
                    toast.success(response.message || 'Cập nhật thành công');

                    setRewards((prev) =>
                        prev.map((r) =>
                            r.attributes.id === editingReward.attributes.id
                                ? (response.data as unknown as IRewardItem)
                                : r
                        )
                    );
                    handleCloseDialog();
                }
            } else {
                const response = await createReward(formData);
                if (response.success && response.data) {
                    toast.success(response.message || 'Tạo thành công');
                    setRewards((prev) => [
                        response.data as unknown as IRewardItem,
                        ...prev,
                    ]);
                    handleCloseDialog();
                }
            }
        } catch (error) {
            handleError(error);
        }
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any

    const paginatedRewards = rewards.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const handleToggleStatus = async (reward: IRewardItem) => {
        try {
            const newStatus =
                reward.attributes.status === 'active' ? 'inactive' : 'active';
            const formData = new FormData();
            formData.append('status', newStatus);

            const response = await updateReward(reward.attributes.id, formData);
            if (response.success) {
                toast.success(
                    response.message || 'Cập nhật trạng thái thành công'
                );
                setRewards((prev) =>
                    prev.map((r) =>
                        r.attributes.id === reward.attributes.id
                            ? (response.data as unknown as IRewardItem)
                            : r
                    )
                );
            }
        } catch (error) {
            handleError(error);
        }
    };

    return (
        <Box p={3}>
            {/* Header */}
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
            >
                <Typography variant="h5" fontWeight="bold">
                    Quản lý phần thưởng
                </Typography>
                <Button
                    sx={styles.btnAdd}
                    variant="contained"
                    startIcon={<AddIcon sx={{ fontSize: '40px !important' }} />}
                    onClick={handleOpenCreate}
                >
                    Thêm phần thưởng
                </Button>
            </Stack>

            {/* Table */}
            <TableContainer
                component={Paper}
                sx={{
                    boxShadow: 'none',
                    border: 'none',
                }}
            >
                <Table
                    sx={{
                        borderCollapse: 'collapse',
                    }}
                >
                    <TableHead>
                        <TableRow>
                            {[
                                'ID',
                                'Hình ảnh',
                                'Tên phần thưởng',
                                'Chi tiết',
                                'Ngày tạo',
                                'Trạng thái',
                                'Hành động',
                            ].map((header) => (
                                <TableCell
                                    key={header}
                                    sx={{
                                        border: 'none',
                                        fontWeight: 600,
                                        fontSize: '18px',
                                        lineHeight: '21.78px',
                                    }}
                                >
                                    {header}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    Đang tải...
                                </TableCell>
                            </TableRow>
                        ) : paginatedRewards.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    Không có dữ liệu
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedRewards.map((reward) => (
                                <TableRow key={reward.id}>
                                    <TableCell>{reward.id}</TableCell>
                                    <TableCell>
                                        {reward.attributes.image?.url ? (
                                            <Box
                                                component="img"
                                                src={
                                                    reward.attributes.image.url
                                                }
                                                alt={reward.attributes.name}
                                                sx={{
                                                    width: 50,
                                                    height: 50,
                                                    objectFit: 'cover',
                                                    borderRadius: 1,
                                                }}
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: 50,
                                                    height: 50,
                                                    backgroundColor: '#f5f5f5',
                                                    borderRadius: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    N/A
                                                </Typography>
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {reward.attributes.name}
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            sx={{
                                                maxWidth: 300,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {reward.attributes.detail}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {new Date(
                                            reward.attributes.created_at
                                        ).toLocaleDateString('vi-VN')}
                                    </TableCell>
                                    <TableCell>
                                        <Switch
                                            checked={
                                                reward.attributes.status ===
                                                'active'
                                            }
                                            onChange={() =>
                                                handleToggleStatus(reward)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            color="primary"
                                            onClick={() =>
                                                handleOpenEdit(reward)
                                            }
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={rewards.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                    labelRowsPerPage="Số dòng mỗi trang:"
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} của ${count}`
                    }
                />
            </TableContainer>

            {/* Dialog Create/Edit */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Typography variant="h6">
                            {editingReward
                                ? 'Cập nhật phần thưởng'
                                : 'Thêm phần thưởng'}
                        </Typography>
                        <IconButton onClick={handleCloseDialog}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <FormProvider methods={methods} onSubmit={onSubmit}>
                    <DialogContent>
                        <Stack spacing={3}>
                            {/* Tên phần thưởng */}
                            <Stack spacing={'7px'}>
                                <RequiredLabel
                                    label="Tên phần thưởng"
                                    sx={{ fontSize: '14px' }}
                                    required={true}
                                />
                                <BaseTextField
                                    autoComplete="off"
                                    name="name"
                                    fullWidth
                                    size="small"
                                    placeholder="Nhập tên phần thưởng"
                                    sx={styleInput}
                                    InputProps={{
                                        inputProps: {
                                            maxLength: 55,
                                        },
                                    }}
                                />
                            </Stack>

                            {/* Hình ảnh */}
                            <Stack spacing={'7px'}>
                                <RequiredLabel
                                    label="Hình ảnh"
                                    sx={{ fontSize: '14px' }}
                                    required={false}
                                />
                                <ImageUpload
                                    name="image"
                                    sx={{ width: '100%', height: 150 }}
                                />
                            </Stack>

                            {/* Chi tiết */}
                            <Stack spacing={'7px'}>
                                <RequiredLabel
                                    label="Chi tiết"
                                    sx={{ fontSize: '14px' }}
                                    required={false}
                                />
                                <BaseTextField
                                    multiline
                                    rows={4}
                                    autoComplete="off"
                                    name="detail"
                                    fullWidth
                                    inputProps={{
                                        maxLength: 255,
                                    }}
                                    placeholder="Nhập chi tiết phần thưởng (tối đa 500 ký tự)"
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            ...styleInput[
                                                '& .MuiInputBase-root'
                                            ],
                                            height: '130px',
                                            borderRadius: '15px',
                                        },
                                    }}
                                />
                            </Stack>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button
                            sx={{
                                ...styles.button,
                                minWidth: '155px',
                                borderWidth: '2px',
                            }}
                            variant="outlined"
                            onClick={handleCloseDialog}
                        >
                            Đóng
                        </Button>
                        <Button
                            sx={{
                                ...styles.button,
                                minWidth: '155px',
                                borderWidth: '2px',
                            }}
                            type="submit"
                            variant="contained"
                        >
                            {editingReward ? 'Cập nhật' : 'Tạo mới'}
                        </Button>
                    </DialogActions>
                </FormProvider>
            </Dialog>
        </Box>
    );
};

const styles = createStyleSheet({
    btnAdd: {
        minWidth: '230px',
        fontSize: '20px',
        textTransform: 'none',
        fontWeight: '700',
        lineHeight: '24.2px',
        borderRadius: '39.06px',
    },
    button: {
        minWidth: '160px',
        minHeight: '36px',
        borderRadius: '40px',
        textTransform: 'none',
        fontSize: '16px',
        fontWeight: 700,
    },
});

export default Reward;
